// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  AmlDecisionResultDTO,
  AmlRequestItemDTO,
  AmlSummaryEntryDTO,
} from '../types/aml';

/**
 * Advisor-side surface of the AML Documentation Request endpoints
 * (resource-server `AmlDocumentationController`). Requests themselves are
 * created via `profileRequestApi.create` with
 * `requestType='AML_DOCUMENTATION'` and
 * `typeSpecificInput={targetClientId, items}`.
 */
export const amlApi = {
  /** The requested documents on an AML request with review state. */
  listItems: async (requestId: string): Promise<AmlRequestItemDTO[]> => {
    const { data } = await api.get<AmlRequestItemDTO[]>(`/requests/${requestId}/aml/items`);
    return data;
  },

  /** Approve an uploaded document. */
  approve: async (requestId: string, itemId: string): Promise<AmlDecisionResultDTO> => {
    const { data } = await api.post<AmlDecisionResultDTO>(
      `/requests/${requestId}/aml/items/${itemId}/decision`,
      { decision: 'APPROVED' },
    );
    return data;
  },

  /** Reject an uploaded document with a client-facing reason. */
  reject: async (
    requestId: string,
    itemId: string,
    reason: string,
  ): Promise<AmlDecisionResultDTO> => {
    const { data } = await api.post<AmlDecisionResultDTO>(
      `/requests/${requestId}/aml/items/${itemId}/decision`,
      { decision: 'REJECTED', reason },
    );
    return data;
  },

  /**
   * Record a document upload on the client's behalf (proxy). Requires a
   * reason + attestation; the uploaded document doubles as the proxy
   * evidence. Audited CRITICAL server-side.
   */
  uploadByProxy: async (
    requestId: string,
    itemId: string,
    file: File,
    reason: string,
    attestation: boolean,
    documentType?: string,
  ): Promise<AmlRequestItemDTO> => {
    const form = new FormData();
    form.append('file', file);
    form.append('reason', reason);
    form.append('attestation', String(attestation));
    if (documentType) form.append('documentType', documentType);
    const { data } = await api.post<AmlRequestItemDTO>(
      `/requests/${requestId}/aml/items/${itemId}/upload-by-proxy`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /** Latest AML state per (client, document type) across every cycle on the profile. */
  summary: async (profileId: string): Promise<AmlSummaryEntryDTO[]> => {
    const { data } = await api.get<AmlSummaryEntryDTO[]>(`/profiles/${profileId}/aml/summary`);
    return data;
  },
};
